import { data } from '@portal/pkg-api/shared'
import { RecurrencesApi } from '@portal/pkg-api/admin'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  EntityCreationRecurrenceBasic,
  EntityCreationRecurrenceDetail,
} from '@portal/pkg-api/gen/recurrences'
import { ChronoField, LocalDate } from '@js-joda/core'

const prefix = 'admin.recurrences'

export const RecurrenceQueries = {
  recurrences: (entityType: number) => ({
    queryKey: [`${prefix}.recurrences`, entityType],
    queryFn: () => data(RecurrencesApi.getRecurrences(entityType)),
  }),
  detail: (recurrenceId: number) => ({
    queryKey: [`${prefix}.recurrenceDetail`, recurrenceId],
    queryFn: () => {
      if (recurrenceId === -1) {
        const cal = {
          freq: 'MONTHLY',
          skip: 'BACKWARD',
          interval: 1,
          bymonthday: LocalDate.now().get(ChronoField.DAY_OF_MONTH),
        }
        return {
          name: '',
          entityNamePrefix: '',
          entityTypeId: -1,
          sortIndex: 1,
          recurDateStart: LocalDate.now().toString(),
          rruleJcal: JSON.stringify(cal),
        }
      }
      return data(RecurrencesApi.getRecurrence(recurrenceId))
    },
  }),
}

export function useRecurrences(entityType: number) {
  return useQuery(RecurrenceQueries.recurrences(entityType))
}

export function useRecurrenceDetail(recurrenceId: number) {
  return useQuery(RecurrenceQueries.detail(recurrenceId))
}

export function useSaveRecurrence() {
  const queryClient = useQueryClient()
  return useMutation<EntityCreationRecurrenceDetail, unknown, EntityCreationRecurrenceDetail>({
    mutationFn: (recurrence) =>
      data(RecurrencesApi.createRecurrences(recurrence, { mercatusNotifyOnError: false })),
    onSuccess: (_data, recurrence) => {
      void queryClient.invalidateQueries(RecurrenceQueries.recurrences(recurrence.entityTypeId))
      void queryClient.invalidateQueries(RecurrenceQueries.detail(recurrence.id!))
    },
  })
}

export function useDeleteRecurrence() {
  const queryClient = useQueryClient()
  return useMutation<void, unknown, EntityCreationRecurrenceBasic>({
    mutationFn: (recurrence) => data(RecurrencesApi.deleteRecurrence(recurrence.id)),
    onSuccess: (_data, recurrence) => {
      void queryClient.invalidateQueries(RecurrenceQueries.recurrences(recurrence.entityTypeId))
      void queryClient.invalidateQueries(RecurrenceQueries.detail(recurrence.id))
    },
  })
}
